import type { EffectStep } from './effect.types';
import type { BuffModifierStat, BuffModifier } from './buff.types';
import type { CraftCost } from './craft.types';
import type { ActionDirectionType, EffectShape, RangeAlgorithm } from './enums';
import type {
  SpellAnimations,
  SpellEffect,
  SpellPreview,
  SpellTimings,
} from './spell.types';
import type { ProjectileConfig } from './projectile.types';

export type TrinketId = string;

/** Progression level of a trinket at the commode. 1 = starting level. */
export type TrinketLevel = number;

/**
 * Character trinket slots. The major trinket occupies a dedicated slot; the 4
 * numbered slots can hold either an active spell, a minor trinket, or both
 * (spell + attached minor trinket).
 */
export type TrinketSlotName = 'major' | 'slot1' | 'slot2' | 'slot3' | 'slot4';

export const PERSO_SLOT_NAMES: readonly TrinketSlotName[] = [
  'slot1',
  'slot2',
  'slot3',
  'slot4',
] as const;

export const PERSO_SLOT_COUNT = PERSO_SLOT_NAMES.length;

/**
 * Inline template for the basic attack carried by a major trinket. The runtime
 * synthesizes a `SpellDefinition` from this template.
 */
export interface TrinketBasicAttack {
  shape?: EffectShape;
  range: number;
  rangeAlgorithm?: RangeAlgorithm;
  /** If set, the synthesized spell scans the facing line up to `range` and
   *  resolves its impact effects on the first entity hit. */
  hitscan?: { range: number };
  /**
   * Direction restriction. Defaults to `ALL`. Cone shapes must be
   * `LIMITED_ALIGNED` (cardinal only) — a diagonal cone resolves to no cells.
   */
  directionType?: ActionDirectionType;
  timings: SpellTimings;
  animations?: SpellAnimations;
  preview?: SpellPreview;
  /** VFX sheet override propagated to the synthesized spell (cf. SpellDefinition.vfx). */
  vfx?: { sheet: number; row?: number };
  cooldownMs?: number;
  /**
   * If set, the synthesized spell launches a real travelling projectile.
   * All template `effects` then resolve on impact.
   */
  projectile?: ProjectileConfig;
  icon?: string;
  /**
   * The basic attack's effects, like any spell. Declare damage explicitly
   * (`dealDamage` / `dealDamageInCells`) plus on-hit buffs/debuffs (Tempo,
   * mark, bleed, shield-on-hit). All are forced to the `action` phase (or
   * `impact` when a `projectile` is set) by `buildSpellFromTemplate`.
   */
  effects?: SpellEffect[];
}

/**
 * Effects of a trinket at a given level. Unified between minor and major trinkets.
 *
 * - `global`: effects applied to the player at the start of each run via
 *   `TrinketService.applyTrinketEffects` (uses the standard `EFFECT_HANDLERS`,
 *   typically `applyBuff` to set passives).
 * - `onAttachedSpell`: effects applied to the instance of the spell equipped
 *   in the same slot as the minor trinket (for major trinkets, this field is
 *   unused). Executed once at run start and modify the spell instance
 *   persistently during the run.
 * - `onUnlock`: effects applied AT level-up time, not at run start. Typically
 *   used for `unlockMinor` (a major trinket level-up unlocks a minor trinket).
 */
export interface TrinketEffects {
  global?: EffectStep[];
  onAttachedSpell?: EffectStep[];
  onUnlock?: EffectStep[];
  /** Bonus de stat de ce niveau, bakés dans les stats de run-start (remplace
   *  l'`applyBuff` vers un porteur `*-lN` stat-pur). */
  modifiers?: BuffModifier[];
}

/**
 * Static definition of a major trinket. Carries the basic attack and the
 * per-level effects.
 *
 * No numeric stats on a major trinket — all stat persistence lives in the
 * character level. See docs/game-design/equipment.md.
 */
export interface TrinketMajorDefinition {
  id: TrinketId;
  name: string;
  icon?: string;
  basicAttack: TrinketBasicAttack;
  maxLevel: TrinketLevel;
  effectsByLevel?: Partial<Record<TrinketLevel, TrinketEffects>>;
  levelUpCostOverrides?: Partial<Record<TrinketLevel, CraftCost>>;
  sellValue: number;
}

/**
 * Static definition of a minor trinket. Mixed-scope effects (see `TrinketEffects`).
 *
 * Player display: `displayTemplate` allows interpolating the effect handler
 * values into a string (e.g. `"+{value} joy damage"`). For exotic cases,
 * omit the template — the UI falls back to a specific description wired on
 * the front-end side.
 */
/** Per-level linear stat growth: flat modifiers are baked into base stats at
 *  run start; only the lifesteal part stays a runtime buff keyed by modelId. */
export interface TrinketStatScalingModifier {
  stat: BuffModifierStat;
  amountPerLevel?: number;
  percentPerLevel?: number;
}

export interface TrinketStatScaling {
  modifiers?: TrinketStatScalingModifier[];
  lifeStealPercentPerLevel?: number;
}

export interface TrinketMinorDefinition {
  id: TrinketId;
  name: string;
  icon?: string;
  /** Linear per-level stat growth (replaces per-tier companion buffs). */
  statScaling?: TrinketStatScaling;
  effectsByLevel?: Partial<Record<TrinketLevel, TrinketEffects>>;
  displayTemplate?: string;
  maxLevel: TrinketLevel;
  levelUpCostOverrides?: Partial<Record<TrinketLevel, CraftCost>>;
  sellValue: number;
  /** Number of boost sockets on this minor trinket. Absent / 0 = no socketing. */
  socketSlots?: number;
}

/**
 * Content of a numbered character slot. A slot can hold an active spell, a
 * minor trinket, or both. Both fields are nullable; a slot where
 * `spellId === null && minorId === null` is empty.
 *
 * - spell only: standard spell effect (cooldown, cast).
 * - minor trinket only: only the trinket's `global` effects apply.
 * - spell + minor trinket: both the trinket's `onAttachedSpell` AND `global`
 *   effects apply to the attached spell.
 */
export interface PlayerSlotContent {
  spellId: string | null;
  minorId: TrinketId | null;
}

export const EMPTY_PLAYER_SLOT: PlayerSlotContent = {
  spellId: null,
  minorId: null,
};
